import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Rocket } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { launchCampaign } from '../../services/api';

export default function LaunchDialog({ open, onOpenChange, campaign, onStarted }) {
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) setTyped(''); }, [open]);

  const members = campaign.members || [];
  const aliases = members.map((m) => m.richMenuAliasId).filter(Boolean);
  const defaultMenu = members.find((m) => m.id === campaign.defaultMenuId);
  const matches = typed === campaign.channelName;

  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!matches || submitting) return;
    setSubmitting(true);
    try {
      const { runId } = await launchCampaign(campaign.id, typed);
      onOpenChange(false);
      onStarted(runId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start the launch');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="bg-[#18181B] border-[#27272A]" data-testid="launch-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Launch now</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">This changes what your LINE users see.</DialogDescription>
          </DialogHeader>
          <ul className="list-disc pl-5 my-4 space-y-1 text-sm text-[#E4E4E7]">
            <li>Publish {members.length} {members.length === 1 ? 'menu' : 'menus'}</li>
            {aliases.length > 0 && <li>Move aliases {aliases.join(', ')}</li>}
            <li>{defaultMenu ? `Set ${defaultMenu.name} as the default menu` : 'Leave the default menu unchanged'}</li>
          </ul>
          <p className="text-sm text-[#A1A1AA] mb-4">LINE is updated immediately; phones refresh on their own schedule.</p>
          <Label htmlFor="launch-confirm-input" className="text-sm">
            Type <span className="font-semibold text-white">{campaign.channelName}</span> to confirm
          </Label>
          <Input
            id="launch-confirm-input"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            className="mt-2 bg-[#09090B] border-[#27272A]"
            data-testid="launch-confirm-input"
          />
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" className="border-[#27272A]" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
            <Button type="submit" className="bg-[#06C755] hover:bg-[#05b34c] text-black" disabled={!matches || submitting} data-testid="launch-confirm">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
              Launch
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
